import { useCallback, useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ArrowsClockwise, ChartBar, Columns, DownloadSimple, MagnifyingGlass, Pause, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import DateRangePresets, { presetToRange } from "@/components/DateRangePresets";

const ALL = "__ALL__";
const COLUMNS_STORAGE_KEY = "publisher-performance-visible-columns-v1";

const NETWORK_LABELS = {
  impact: "Impact",
  partnerize: "Partnerize",
  skimlinks: "Skimlinks",
  mrge: "mrge",
  cj: "CJ",
  admedia: "AdMedia",
  pubadhub: "PubAdHub",
  pubadhub_kevin: "PubAdHub 2",
  flexoffers: "FlexOffers",
  shopnomix_kevin: "Shopnomix InMobi",
  siteplug: "Siteplug",
  shopnomix: "Shopnomix",
  pronomix: "Pronomix",
  yieldkit: "Yieldkit",
};

const PERF_COLUMNS = [
  { id: "pubs", label: "Pubs", defaultOn: true, align: "left" },
  { id: "brands", label: "Brands", defaultOn: true, align: "left" },
  { id: "network", label: "Feed partner", defaultOn: true, align: "left" },
  { id: "clicks", label: "Clicks", defaultOn: true, align: "right" },
  { id: "conversions", label: "Conversions", defaultOn: true, align: "right" },
  { id: "sales", label: "Sales", defaultOn: true, align: "right" },
  { id: "network_commission", label: "Network Commission", defaultOn: true, align: "right" },
  { id: "network_pct", label: "Network %", defaultOn: false, align: "right", optional: true },
  { id: "pub_commission_pct", label: "Pub Commission %", defaultOn: false, align: "right", optional: true },
  { id: "pub_commission", label: "Pub Commission", defaultOn: true, align: "right" },
  { id: "pub_earning", label: "Pub Earning", defaultOn: true, align: "right" },
  { id: "margin", label: "Margin", defaultOn: true, align: "right" },
  { id: "margin_pct", label: "Margin %", defaultOn: false, align: "right", optional: true },
  { id: "state", label: "State", defaultOn: true, align: "center" },
];

function loadVisibleColumns() {
  const defaults = Object.fromEntries(PERF_COLUMNS.map((c) => [c.id, c.defaultOn]));
  try {
    const raw = localStorage.getItem(COLUMNS_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return defaults;
    return { ...defaults, ...parsed };
  } catch {
    return defaults;
  }
}

function fmtMoney(n) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return "—";
  return `${Number(v).toFixed(2)}%`;
}

export default function AdminPublisherPerformance() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({
    clicks: 0, sales: 0, commission: 0,
    network_commission: 0, pub_commission: 0, pub_earning: 0, margin: 0,
  });
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);
  const [facets, setFacets] = useState({ networks: [], brands: [], publishers: [], actions: [] });
  const [visibleCols, setVisibleCols] = useState(loadVisibleColumns);

  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [publisherId, setPublisherId] = useState("");
  const [brand, setBrand] = useState("");
  const [network, setNetwork] = useState("");
  const [status, setStatus] = useState("all");
  const [activityOnly, setActivityOnly] = useState(true);
  const [dateRange, setDateRange] = useState(() => {
    const r = presetToRange("last_7");
    return { preset: "last_7", start: r.start, end: r.end };
  });

  const pageSize = 50;

  const toggleColumn = useCallback((id) => {
    setVisibleCols((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(next));
      } catch { /* ignore */ }
      return next;
    });
  }, []);

  const activeColumns = useMemo(
    () => PERF_COLUMNS.filter((c) => visibleCols[c.id]),
    [visibleCols],
  );

  useEffect(() => {
    API.get("/admin/publisher-performance/facets")
      .then((r) => setFacets(r?.data || {}))
      .catch(() => { /* degrade */ });
  }, []);

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        activity_only: activityOnly,
      };
      if (search.trim()) params.search = search.trim();
      if (publisherId) params.publisher_id = publisherId;
      if (brand) params.brand = brand;
      if (network) params.network = network;
      if (status && status !== "all") params.status = status;
      if (dateRange.preset === "all") {
        params.all_time = true;
      } else {
        if (dateRange.start) params.start_date = dateRange.start;
        if (dateRange.end) params.end_date = dateRange.end;
      }
      const { data } = await API.get("/admin/publisher-performance", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setTotals(data.totals || {
        clicks: 0, sales: 0, commission: 0,
        network_commission: 0, pub_commission: 0, pub_earning: 0, margin: 0,
      });
      if (
        data.date_range_defaulted
        && dateRange.preset !== "all"
        && data.effective_start_date
        && data.effective_end_date
      ) {
        setDateRange({
          preset: "custom",
          start: data.effective_start_date,
          end: data.effective_end_date,
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Publisher Performance");
    } finally {
      setLoading(false);
    }
  }, [page, search, publisherId, brand, network, status, activityOnly, dateRange]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => { setPage(1); }, [publisherId, brand, network, status, activityOnly, dateRange]);

  const downloadCsv = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (publisherId) params.set("publisher_id", publisherId);
      if (brand) params.set("brand", brand);
      if (network) params.set("network", network);
      if (status && status !== "all") params.set("status", status);
      params.set("activity_only", activityOnly ? "true" : "false");
      if (dateRange.preset === "all") {
        params.set("all_time", "true");
      } else {
        if (dateRange.start) params.set("start_date", dateRange.start);
        if (dateRange.end) params.set("end_date", dateRange.end);
      }
      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const resp = await fetch(`${baseURL}/admin/publisher-performance/export.csv?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `publisher-performance-${dateRange.start || "all"}-to-${dateRange.end || "all"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const brandOptions = useMemo(() => {
    const list = facets.brands || [];
    if (!search.trim()) return list.slice(0, 200);
    const needle = search.toLowerCase();
    return list.filter((b) => b.toLowerCase().includes(needle)).slice(0, 200);
  }, [facets.brands, search]);

  const toggleAssignmentStatus = async (row) => {
    if (!row?.publisher_id || !row?.brand_id) {
      toast.error("Missing publisher or brand on this row");
      return;
    }
    const rowKey = `${row.publisher_id}-${row.brand_id}`;
    const current = (row.action || "active").toLowerCase();
    const next = current === "paused" ? "active" : "paused";
    setTogglingKey(rowKey);
    try {
      await API.put(
        `/admin/publisher-brands/${row.publisher_id}/${row.brand_id}/status`,
        { status: next },
      );
      toast.success(
        next === "paused"
          ? `Paused ${row.brand_name || "brand"} for ${row.publisher_email || "publisher"}`
          : `Reactivated ${row.brand_name || "brand"} for ${row.publisher_email || "publisher"}`,
      );
      fetchRows();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update status");
    } finally {
      setTogglingKey(null);
    }
  };

  const networkCommission = totals.network_commission ?? totals.commission ?? 0;

  return (
    <div className="p-8 space-y-6" data-testid="admin-publisher-performance-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">ANALYTICS</p>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <ChartBar size={28} className="text-[#002FA7]" weight="fill" />
            Publisher Performance
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Publisher × brand × feed partner — clicks, sales, network commission, pub earning, and margin.
            Default view is last 7 days; choose <strong>All time</strong> for lifetime metrics.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button variant="outline" size="sm" onClick={fetchRows} disabled={loading}>
            <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={downloadCsv} disabled={exporting || total === 0}>
            <DownloadSimple size={14} className="mr-1.5" />
            {exporting ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm">
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Rows</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{total.toLocaleString()}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Clicks</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{totals.clicks?.toLocaleString() ?? 0}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Conversions</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">{(totals.conversions ?? 0).toLocaleString()}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales</div>
          <div className="font-heading text-xl font-bold text-slate-900 mt-1">${fmtMoney(totals.sales)}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Network Comm</div>
          <div className="font-heading text-xl font-bold text-slate-800 mt-1">${fmtMoney(networkCommission)}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Pub Commission</div>
          <div className="font-heading text-xl font-bold text-slate-700 mt-1">${fmtMoney(totals.pub_commission)}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Pub Earning</div>
          <div className="font-heading text-xl font-bold text-[#002FA7] mt-1">${fmtMoney(totals.pub_earning)}</div>
        </div>
        <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Margin</div>
          <div className={`font-heading text-xl font-bold mt-1 ${(totals.margin || 0) >= 0 ? "text-emerald-600" : "text-red-500"}`}>
            ${fmtMoney(totals.margin)}
          </div>
        </div>
      </div>

      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="publisher-performance-filters">
        <div className="relative max-w-xl">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search publisher, brand, or feed partner…"
            className="pl-8 h-9 bg-white"
            data-testid="publisher-performance-search"
          />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[180px]">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Publisher</Label>
            <Select value={publisherId || ALL} onValueChange={(v) => setPublisherId(v === ALL ? "" : v)}>
              <SelectTrigger className="h-9 bg-white" data-testid="publisher-performance-publisher">
                <SelectValue placeholder="All publishers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All publishers</SelectItem>
                {(facets.publishers || []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[180px]">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Brand</Label>
            <Select value={brand || ALL} onValueChange={(v) => setBrand(v === ALL ? "" : v)}>
              <SelectTrigger className="h-9 bg-white" data-testid="publisher-performance-brand">
                <SelectValue placeholder="All brands" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ALL}>All brands</SelectItem>
                {brandOptions.map((b) => (
                  <SelectItem key={b} value={b}>{b}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Feed partner</Label>
            <Select value={network || ALL} onValueChange={(v) => setNetwork(v === ALL ? "" : v)}>
              <SelectTrigger className="h-9 bg-white" data-testid="publisher-performance-network">
                <SelectValue placeholder="All feeds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All feeds</SelectItem>
                {(facets.networks || []).map((n) => (
                  <SelectItem key={n} value={n}>{NETWORK_LABELS[n] || n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">State</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="h-9 bg-white" data-testid="publisher-performance-state">
                <SelectValue placeholder="All" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="paused">Paused</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[280px]">
            <Label className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Date range</Label>
            <DateRangePresets
              preset={dateRange.preset}
              start={dateRange.start}
              end={dateRange.end}
              onChange={setDateRange}
              testId="publisher-performance-date"
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-600 pb-1 cursor-pointer">
            <input
              type="checkbox"
              checked={activityOnly}
              onChange={(e) => setActivityOnly(e.target.checked)}
              className="rounded border-slate-300"
              data-testid="publisher-performance-activity-only"
            />
            Only rows with activity
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 ml-auto" data-testid="publisher-performance-columns">
                <Columns size={14} className="mr-1.5" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Show columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {PERF_COLUMNS.filter((c) => !c.optional).map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={!!visibleCols[c.id]}
                  onCheckedChange={() => toggleColumn(c.id)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-slate-500 font-normal">
                Extra % fields
              </DropdownMenuLabel>
              {PERF_COLUMNS.filter((c) => c.optional).map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.id}
                  checked={!!visibleCols[c.id]}
                  onCheckedChange={() => toggleColumn(c.id)}
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`perf-col-toggle-${c.id}`}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="border border-slate-200 rounded-md overflow-hidden bg-white overflow-x-auto">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {activeColumns.map((c) => (
                <th
                  key={c.id}
                  className={`${
                    c.align === "right" ? "text-right" : c.align === "center" ? "text-center" : "text-left"
                  } px-4 py-3 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold whitespace-nowrap`}
                >
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={Math.max(activeColumns.length, 1)} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={Math.max(activeColumns.length, 1)} className="px-4 py-12 text-center text-slate-500">No rows match the selected filters.</td></tr>
            )}
            {!loading && items.map((r) => {
              const rowKey = `${r.publisher_id}-${r.brand_id}`;
              const isPaused = (r.action || "active").toLowerCase() === "paused";
              const isToggling = togglingKey === rowKey;
              const netComm = r.network_commission ?? r.commission ?? 0;
              const marginVal = Number(r.margin || 0);
              const marginTone = marginVal >= 0 ? "text-emerald-600" : "text-red-500";
              const cell = (colId) => {
                switch (colId) {
                  case "pubs":
                    return (
                      <td key={colId} className="px-4 py-2.5 text-[11px] text-slate-800">
                        <div className="font-semibold">{r.publisher_email || "—"}</div>
                        {r.publisher_sub_id ? (
                          <div className="font-mono text-[10px] text-slate-400">{r.publisher_sub_id}</div>
                        ) : null}
                      </td>
                    );
                  case "brands":
                    return <td key={colId} className="px-4 py-2.5 text-[11px] font-semibold text-slate-900">{r.brand_name || "—"}</td>;
                  case "network":
                    return (
                      <td key={colId} className="px-4 py-2.5">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-800">
                          {NETWORK_LABELS[r.network] || r.network || "—"}
                        </span>
                      </td>
                    );
                  case "clicks":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-800">{r.clicks?.toLocaleString() ?? 0}</td>;
                  case "conversions":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-800">{(r.conversions ?? 0).toLocaleString()}</td>;
                  case "sales":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-800">${fmtMoney(r.sales)}</td>;
                  case "network_commission":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-800">${fmtMoney(netComm)}</td>;
                  case "network_pct":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-500">{fmtPct(r.network_pct)}</td>;
                  case "pub_commission_pct":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-[#002FA7]">{fmtPct(r.pub_commission_pct)}</td>;
                  case "pub_commission":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] text-slate-700">${fmtMoney(r.pub_commission)}</td>;
                  case "pub_earning":
                    return <td key={colId} className="px-4 py-2.5 text-right font-mono text-[11px] font-semibold text-[#002FA7]">${fmtMoney(r.pub_earning)}</td>;
                  case "margin":
                    return <td key={colId} className={`px-4 py-2.5 text-right font-mono text-[11px] font-semibold ${marginTone}`}>${fmtMoney(r.margin)}</td>;
                  case "margin_pct":
                    return <td key={colId} className={`px-4 py-2.5 text-right font-mono text-[11px] ${marginTone}`}>{fmtPct(r.margin_pct)}</td>;
                  case "state":
                    return (
                      <td key={colId} className="px-4 py-2.5 text-center">
                        <div className="inline-flex items-center gap-1.5">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                            isPaused
                              ? "bg-amber-100 text-amber-800"
                              : "bg-emerald-100 text-emerald-700"
                          }`}>
                            {isPaused ? "Paused" : "Active"}
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-6 px-2 text-[10px] font-bold uppercase tracking-wide"
                            disabled={isToggling || !r.brand_id}
                            onClick={() => toggleAssignmentStatus(r)}
                            data-testid={`publisher-performance-toggle-${rowKey}`}
                          >
                            {isToggling ? (
                              "…"
                            ) : isPaused ? (
                              <><Play size={11} className="mr-0.5" weight="fill" /> Activate</>
                            ) : (
                              <><Pause size={11} className="mr-0.5" weight="fill" /> Pause</>
                            )}
                          </Button>
                        </div>
                      </td>
                    );
                  default:
                    return null;
                }
              };
              return (
                <tr key={`${r.publisher_id}-${(r.brand_name || "").toLowerCase()}-${r.network || ""}-${r.brand_id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  {activeColumns.map((c) => cell(c.id))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-[11px] text-slate-500">
        <div>
          {total.toLocaleString()} row{total === 1 ? "" : "s"} · page <span className="font-mono font-bold">{page}</span> / <span className="font-mono">{Math.max(1, totalPages)}</span>
        </div>
        <div className="flex gap-1.5">
          <Button variant="outline" size="sm" disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Prev</Button>
          <Button variant="outline" size="sm" disabled={loading || page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button>
        </div>
      </div>
    </div>
  );
}
